import moment from 'moment';
import React, { useState } from 'react';
import { HiOutlineClipboardCopy, HiCheckCircle } from 'react-icons/hi';
import {
  DateCellRenderer,
  createTextRenderer,
  createBoldTextRenderer,
  styledDefaultColDef,
} from '../../../extras/cellRenderers';

// ─── Coupon Code cell renderer (enterprise-grade) ───────────────
function CouponCodeRenderer(props) {
  const code = props.data?.couponCode;
  const [copied, setCopied] = useState(false);

  if (!code) return <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%' }}>
      <span
        style={{
          fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
          fontSize: 12,
          fontWeight: 600,
          color: '#1E293B',
          backgroundColor: '#F1F5F9',
          border: '1px solid #E2E8F0',
          borderRadius: 4,
          padding: '2px 8px',
          letterSpacing: '0.3px',
          lineHeight: 1.5,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 160,
        }}
        title={code}
      >
        {code}
      </span>
      <button
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy coupon code'}
        title={copied ? 'Copied!' : 'Copy code'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 2,
          flexShrink: 0,
          transition: 'opacity 150ms ease',
          opacity: 0.7,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.7; }}
      >
        {copied ? (
          <HiCheckCircle style={{ fontSize: 16, color: '#10B981' }} />
        ) : (
          <HiOutlineClipboardCopy style={{ fontSize: 16, color: '#64748B' }} />
        )}
      </button>
    </div>
  );
}

const dateFilterParams = {
  comparator: (filterLocalDateAtMidnight, cellValue) => {
    cellValue = moment(cellValue).format('DD/MM/YYYY');
    const dateAsString = cellValue;

    if (dateAsString == null) {
      return 0;
    }

    const dateParts = dateAsString.split('/');
    const year = Number(dateParts[2]);
    const month = Number(dateParts[1]) - 1;
    const day = Number(dateParts[0]);
    const cellDate = new Date(year, month, day);

    if (cellDate < filterLocalDateAtMidnight) {
      return -1;
    } else if (cellDate > filterLocalDateAtMidnight) {
      return 1;
    }
    return 0;
  },
};

export const columnDefs = [
  {
    headerName: 'Coupon Code',
    field: 'couponCode',
    headerTooltip: 'Coupon Code',
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    width: 250,
    cellRenderer: CouponCodeRenderer,
  },
  {
    headerName: 'Discount Amount (in Paise)',
    field: 'discountAmount',
    headerTooltip: 'Discount Amount (in Paise)',
    cellRenderer: createBoldTextRenderer((p) => p.data?.discountAmount),
    filter: 'agNumberColumnFilter',
    filterParams: {
      allowedCharPattern: '\\d\\-\\,',
      numberParser: (text) => {
        return text == null ? null : parseFloat(text.replace(',', '.'));
      },
    },
  },
  {
    headerName: 'Goal Type',
    field: 'goalType',
    headerTooltip: 'Goal Type',
    cellRenderer: createTextRenderer((p) => p.data?.goalType),
  },
  {
    headerName: 'Plan Type',
    field: 'planType',
    headerTooltip: 'Plan Type',
    cellRenderer: createTextRenderer((p) => p.data?.planType),
  },
  {
    headerName: 'Sub Plan Type',
    field: 'subPlanType',
    headerTooltip: 'Sub Plan Type',
    cellRenderer: createTextRenderer((p) => p.data?.subPlanType),
  },
  {
    headerName: 'Valid For Users',
    field: 'validForUsers',
    headerTooltip: 'Valid For Users',
    autoHeight: true,
    width: 250,
    cellRenderer: (params) => {
      const users = params.data?.validForUsers || [];
      if (!users.length) return <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
          {users.map((user) => (
            <span key={user} style={{ fontSize: 12.5, color: '#374151' }}>
              {user}
            </span>
          ))}
        </div>
      );
    },
    filter: 'agTextColumnFilter',
    filterParams: {
      suppressAndOrCondition: true,
    },
  },
  {
    headerName: 'Expiry Date',
    field: 'expiryDate',
    headerTooltip: 'Expiry Date',
    cellRenderer: DateCellRenderer,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    pinned: 'right',
  },
  {
    headerName: 'SignedUp Before',
    field: 'beforeSignupDate',
    headerTooltip: 'SignedUp Before',
    cellRenderer: DateCellRenderer,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    pinned: 'right',
  },
  {
    headerName: 'Created On',
    field: 'createdAt',
    cellRenderer: DateCellRenderer,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    hide: true,
  },
  {
    headerName: 'Modified On',
    field: 'updatedAt',
    cellRenderer: DateCellRenderer,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    hide: true,
  },
  {
    field: 'createdBy',
    hide: true,
  },
  { field: 'modifiedBy', hide: true },
  {
    field: 'owner',
    cellRenderer: createTextRenderer((p) => p.data?.owner),
  },
];

export const defaultColDef = {
  ...styledDefaultColDef,
  filter: true,
};
